import React, { lazy } from "react";
import permissions from "./permissions";
import AuthGuard from "gaurds/AuthGuard";
import GuestGuard from "gaurds/GuestGuard";
import AdminLayout from "layouts/AdminLayout";
import { Route } from "react-router-dom";
import PermissionGuard from "../gaurds/PermissionGuard";

const Login = lazy(() => import("../pages/login/Login"));

const Home = lazy(() => import("../pages/home/Home"));

// handle lazy loading //
// ** Inbound ** //
const InboundOrders = lazy(() =>
  import("../pages/orders/pages/InboundOrder/pages")
);
const SerializedInbound = lazy(() =>
  import("../pages/orders/pages/InboundOrder/pages/serialized")
);
const NonSerializedInbound = lazy(() =>
  import("../pages/orders/pages/InboundOrder/pages/nonSerialized")
);
const ScanInboundOrder = lazy(() =>
  import(
    "../pages/orders/pages/InboundOrder/pages/serialized/receiveByOrder/receiveOrder"
  )
);
const OrderSerialGen = lazy(() =>
  import("../pages/orders/pages/InboundOrder/pages/OrderSerialGen")
);

const AddInboundOrder = lazy(() =>
  import("../pages/orders/pages/InboundOrder/pages/receiveByOrder/add")
);
const AddSerializedOrder = lazy(() =>
  import(
    "../pages/orders/pages/InboundOrder/pages/serialized/receiveByOrder/add"
  )
);
const AddNonSerializedOrder = lazy(() =>
  import(
    "../pages/orders/pages/InboundOrder/pages/nonSerialized/receiveByOrder/add"
  )
);
const EditInboundOrder = lazy(() =>
  import("../pages/orders/pages/InboundOrder/pages/receiveByOrder/edit")
);
const EditSerializedOrder = lazy(() =>
  import(
    "../pages/orders/pages/InboundOrder/pages/serialized/receiveByOrder/edit"
  )
);
const EditNonSerializedOrder = lazy(() =>
  import(
    "../pages/orders/pages/InboundOrder/pages/nonSerialized/receiveByOrder/edit"
  )
);
// const SplitInboundOrder = lazy(() => import("../pages/orders/pages/InboundOrder/pages/receiveByOrder/split"))
const ReceiveOrder = lazy(() =>
  import("../pages/orders/pages/InboundOrder/pages/receiveByOrder/receiveOrder")
);
const ReceiveNonSerialized = lazy(() =>
  import(
    "../pages/orders/pages/InboundOrder/pages/nonSerialized/receiveByOrder/receiveOrder"
  )
);
const ReleaseToQuality = lazy(() =>
  import(
    "../pages/orders/pages/InboundOrder/pages/receiveByOrder/releaseToQuality"
  )
);

const Decommission = lazy(() => import("../pages/decommission/Decommission"));
const Move = lazy(() => import("../pages/move/Move"));

const AdhocWithoutSupplier = lazy(() =>
  import("../pages/orders/pages/InboundOrder/pages/adhoc/withoutSupplier")
);

// ***************** //

const OutboundOrders = lazy(() =>
  import("../pages/orders/pages/OutboundOrder/pages")
);
const AddOutboundOrder = lazy(() =>
  import("../pages/orders/pages/OutboundOrder/pages/shipOutByOrder/add")
);
const EditOutboundOrder = lazy(() =>
  import("../pages/orders/pages/OutboundOrder/pages/shipOutByOrder/edit")
);
const ShipOutOrder = lazy(() =>
  import(
    "../pages/orders/pages/OutboundOrder/pages/shipOutByOrder/shipOutOrder"
  )
);

const ScanOutboundOrder = lazy(() =>
  import("../pages/orders/pages/OutboundOrder/pages/scan")
);

// *** INBOUND  *** //

const Files = lazy(() => import("../pages/files/Files"));
const AddFile = lazy(() => import("../pages/files/AddFile"));
const Partners = lazy(() => import("../pages/partners/Partners"));
const AddPartner = lazy(() => import("../pages/partners/AddPartner"));
const EditPartner = lazy(() => import("../pages/partners/EditPartner"));

const Products = lazy(() => import("../pages/products/Products"));
const AddProduct = lazy(() => import("../pages/products/AddProduct"));
const EditProduct = lazy(() => import("../pages/products/EditProduct"));

const SKUs = lazy(() => import("../pages/skus/SKUs"));
const AddSKU = lazy(() => import("../pages/skus/AddSKU"));

const Mapping = lazy(() => import("../pages/inventory/Mapping"));
const AddMapping = lazy(() => import("../pages/inventory/AddMapping"));

const AvailabilityReport = lazy(() =>
  import("../pages/reports/AvailabilityReport")
);
const ActiveItems = lazy(() => import("../pages/reports/ActiveItems"));
const ExpiredItems = lazy(() => import("../pages/reports/ExpiredItems"));
const ShippingReport = lazy(() => import("../pages/reports/ShippingReport"));
const AuditTrailReport = lazy(() =>
  import("../pages/reports/history/AuditTrailReport")
);
const SupplierReport = lazy(() => import("../pages/reports/SupplierReport"));
const QuantityReport = lazy(() => import("../pages/reports/QuantityReport"));
const DecantingReports = lazy(() =>
  import("../pages/reports/DecantingReports")
);
const BatchReport = lazy(() => import("../pages/reports/BatchReport"));
const BatchStatus = lazy(() => import("../pages/reports/BatchStatus"));
const NonSerializedStatus = lazy(() =>
  import("../pages/reports/NonSerializedStatus")
);

const Roles = lazy(() => import("../pages/roles/Roles"));
const AddRole = lazy(() => import("../pages/roles/AddRole"));
const EditRole = lazy(() => import("../pages/roles/EditRole"));
const DuplicateRole = lazy(() => import("../pages/roles/DuplicateRole"));

const Users = lazy(() => import("../pages/users/Users"));
const AddUser = lazy(() => import("../pages/users/AddUser"));
const EditUser = lazy(() => import("../pages/users/EditUser"));

const VerifyStatus = lazy(() => import("../pages/verifyStatus/VerifyStatus"));

const AdminPanel = lazy(() => import("../pages/admin-panel/AdminPanel"));

const TenantSettings = lazy(() =>
  import("pages/admin-panel/TenantSettings/index.js")
);

const ResetPassword = lazy(() =>
  import("../pages/reset-password/ResetPassword")
);

const SerialGenerator = lazy(() =>
  import("../pages/serialGenerator/SerialGenerator")
);
const Pack = lazy(() => import("../pages/pack/Pack"));
const PackScanOutbound = lazy(() =>
  import("../pages/pack/components/byOrder/PackScanOutbound")
);
const Unpack = lazy(() => import("../pages/unpack/Unpack"));

const Area = lazy(() => import("../pages/capacity"));
const AddArea = lazy(() => import("../pages/capacity/pages/AddArea"));
const EditArea = lazy(() => import("../pages/capacity/pages/EditArea.js"));

const Error = lazy(() => import("../pages/error/Error"));

const routes = () => {
  return (
    <>
      <Route
        path='/'
        element={
          <GuestGuard>
            <Login />
          </GuestGuard>
        }
      />
      <Route
        path='/home'
        element={
          <AuthGuard>
            <AdminLayout>
              <Home />
            </AdminLayout>
          </AuthGuard>
        }
      />
      {/* <Route
        path="/inbound-orders"
        element={
          <AuthGuard>
            <AdminLayout>
              <PermissionGuard permissions={[permissions.InboundView]}>
                <InboundOrders />
              </PermissionGuard>
            </AdminLayout>
          </AuthGuard>
        }
      /> */}
      <Route
        path='/inbound-orders/serialized'
        element={
          <AuthGuard>
            <AdminLayout>
              <PermissionGuard permissions={[permissions.InboundView]}>
                <SerializedInbound />
              </PermissionGuard>
            </AdminLayout>
          </AuthGuard>
        }
      />
      <Route
        path='/inbound-orders/nonSerialized'
        element={
          <AuthGuard>
            <AdminLayout>
              <PermissionGuard permissions={[permissions.InboundView]}>
                <NonSerializedInbound />
              </PermissionGuard>
            </AdminLayout>
          </AuthGuard>
        }
      />
      {/* <Route
        path="/inbound-orders/:id/scan"
        element={
          <AuthGuard>
            <AdminLayout>
              <PermissionGuard
                permissions={[
                  permissions.InboundAdhoc_ReleaseToQuality,
                  permissions.InboundAccept,
                  permissions.InboundReject,
                ]}
              >
                <ScanInboundOrder />
              </PermissionGuard>
            </AdminLayout>
          </AuthGuard>
        }
      /> */}
      <Route
        path='/inbound-orders/:id/serialized/receiveOrder'
        element={
          <AuthGuard>
            <AdminLayout>
              <PermissionGuard
                permissions={[
                  permissions.InboundAdhoc_ReleaseToQuality,
                  permissions.InboundAccept,
                  permissions.InboundReject,
                ]}
              >
                <ScanInboundOrder />
              </PermissionGuard>
            </AdminLayout>
          </AuthGuard>
        }
      />
      <Route
        path='/inbound-orders/:id/nonSerialized/receiveOrder'
        element={
          <AuthGuard>
            <AdminLayout>
              <PermissionGuard
                permissions={[
                  permissions.InboundAdhoc_ReleaseToQuality,
                  permissions.InboundAccept,
                  permissions.InboundReject,
                ]}
              >
                <ReceiveNonSerialized />
              </PermissionGuard>
            </AdminLayout>
          </AuthGuard>
        }
      />
      <Route
        path='/pack'
        element={
          <AuthGuard>
            <AdminLayout>
              <PermissionGuard permissions={[permissions.Disaggregation]}>
                <Pack />
              </PermissionGuard>
            </AdminLayout>
          </AuthGuard>
        }
      />
      <Route
        path='/pack/:id/Order Pack'
        element={
          <AuthGuard>
            <AdminLayout>
              <PermissionGuard permissions={[permissions.Disaggregation]}>
                <PackScanOutbound />
              </PermissionGuard>
            </AdminLayout>
          </AuthGuard>
        }
      />
      <Route
        path='/unpack'
        element={
          <AuthGuard>
            <AdminLayout>
              <PermissionGuard permissions={[permissions.Disaggregation]}>
                <Unpack />
              </PermissionGuard>
            </AdminLayout>
          </AuthGuard>
        }
      />
      <Route
        path='/area'
        element={
          <AuthGuard>
            <AdminLayout>
              <PermissionGuard permissions={[permissions.Disaggregation]}>
                <Area />
              </PermissionGuard>
            </AdminLayout>
          </AuthGuard>
        }
      />
      <Route
        path='/area/add-area'
        element={
          <AuthGuard>
            <AdminLayout>
              <PermissionGuard permissions={[permissions.Disaggregation]}>
                <AddArea />
              </PermissionGuard>
            </AdminLayout>
          </AuthGuard>
        }
      />
      <Route
        path='/area/:id/edit'
        element={
          <AuthGuard>
            <AdminLayout>
              <PermissionGuard permissions={[permissions.Disaggregation]}>
                <EditArea />
              </PermissionGuard>
            </AdminLayout>
          </AuthGuard>
        }
      />
      <Route
        path='/inbound-orders/serialized/add'
        element={
          <AuthGuard>
            <AdminLayout>
              <PermissionGuard permissions={[permissions.InboundCreateOrder]}>
                <AddSerializedOrder />
              </PermissionGuard>
            </AdminLayout>
          </AuthGuard>
        }
      />
      <Route
        path='/inbound-orders/nonSerialized/add'
        element={
          <AuthGuard>
            <AdminLayout>
              <PermissionGuard permissions={[permissions.InboundCreateOrder]}>
                <AddNonSerializedOrder />
              </PermissionGuard>
            </AdminLayout>
          </AuthGuard>
        }
      />
      {/* <Route
        path="/inbound-orders/add"
        element={
          <AuthGuard>
            <AdminLayout>
              <PermissionGuard permissions={[permissions.InboundCreateOrder]}>
                <AddInboundOrder />
              </PermissionGuard>
            </AdminLayout>
          </AuthGuard>
        }
      /> */}
      {/* <Route
        path="/inbound-orders/:id/edit"
        element={
          <AuthGuard>
            <AdminLayout>
              <PermissionGuard permissions={[permissions.InboundUpdateOrder]}>
                <EditInboundOrder />
              </PermissionGuard>
            </AdminLayout>
          </AuthGuard>
        }
      /> */}
      <Route
        path='/inbound-orders/serialized/:id/edit'
        element={
          <AuthGuard>
            <AdminLayout>
              <PermissionGuard permissions={[permissions.InboundUpdateOrder]}>
                <EditSerializedOrder />
              </PermissionGuard>
            </AdminLayout>
          </AuthGuard>
        }
      />
      <Route
        path='/inbound-orders/nonSerialized/:id/edit'
        element={
          <AuthGuard>
            <AdminLayout>
              <PermissionGuard permissions={[permissions.InboundUpdateOrder]}>
                <EditNonSerializedOrder />
              </PermissionGuard>
            </AdminLayout>
          </AuthGuard>
        }
      />
      <Route
        path='/inbound-orders/:id/serial-generator'
        element={
          <AuthGuard>
            <AdminLayout>
              <PermissionGuard permissions={[permissions.InboundView]}>
                <OrderSerialGen />
              </PermissionGuard>
            </AdminLayout>
          </AuthGuard>
        }
      />
      {/* <Route
        path="/inbound-orders/:id/split"
        element={
          <AuthGuard>
            <AdminLayout>
              <PermissionGuard permissions={[permissions.InboundUpdateOrder]}>
                <SplitInboundOrder />
              </PermissionGuard>
            </AdminLayout>
          </AuthGuard>
        }
      /> */}
      {/* <Route
        path="/inbound-orders/:id/receiveOrder"
        element={
          <AuthGuard>
            <AdminLayout>
              <PermissionGuard
                permissions={[
                  permissions.InboundReleaseToQuality,
                  permissions.InboundAccept,
                  permissions.InboundReject,
                ]}
              >
                <ReceiveOrder />
              </PermissionGuard>
            </AdminLayout>
          </AuthGuard>
        }
      /> */}
      {/* <Route
        path="/inbound-orders/:id/releaseToQuality"
        element={
          <AuthGuard>
            <AdminLayout>
              <PermissionGuard
                permissions={[
                  permissions.InboundReleaseToQuality,
                  // permissions.InboundAccept,
                  permissions.InboundReject,
                ]}
              >
                <ReleaseToQuality />
              </PermissionGuard>
            </AdminLayout>
          </AuthGuard>
        }
      /> */}
      {/* <Route
        path="/inbound-orders/adhoc-without-supplier"
        element={
          <AuthGuard>
            <AdminLayout>
              <PermissionGuard permissions={[permissions.InboundAccept]}>
                <AdhocWithoutSupplier />
              </PermissionGuard>
            </AdminLayout>
          </AuthGuard>
        }
      /> */}
      <Route
        path='/outbound-orders'
        element={
          <AuthGuard>
            <AdminLayout>
              <PermissionGuard permissions={[permissions.OutboundView]}>
                <OutboundOrders />
              </PermissionGuard>
            </AdminLayout>
          </AuthGuard>
        }
      />
      <Route
        path='/outbound-orders/add'
        element={
          <AuthGuard>
            <AdminLayout>
              <PermissionGuard permissions={[permissions.OutboundCreateOrder]}>
                <AddOutboundOrder />
              </PermissionGuard>
            </AdminLayout>
          </AuthGuard>
        }
      />
      <Route
        path='/outbound-orders/:id/shipoutOrder'
        element={
          <AuthGuard>
            <AdminLayout>
              <PermissionGuard permissions={[permissions.OutboundShipOut]}>
                <ShipOutOrder />
              </PermissionGuard>
            </AdminLayout>
          </AuthGuard>
        }
      />
      <Route
        path='/outbound-orders/:id/edit'
        element={
          <AuthGuard>
            <AdminLayout>
              <PermissionGuard permissions={[permissions.OutboundUpdateOrder]}>
                <EditOutboundOrder />
              </PermissionGuard>
            </AdminLayout>
          </AuthGuard>
        }
      />
      <Route
        path='/outbound-orders/:id/scan'
        element={
          <AuthGuard>
            <AdminLayout>
              <PermissionGuard
                permissions={[
                  permissions.OutboundAdhoc_ShipOut,
                  permissions.OutboundShipOut,
                ]}
              >
                <ScanOutboundOrder />
              </PermissionGuard>
            </AdminLayout>
          </AuthGuard>
        }
      />
      <Route
        path='/files'
        element={
          <AuthGuard>
            <AdminLayout>
              <PermissionGuard permissions={[permissions.InboundFileView]}>
                <Files />
              </PermissionGuard>
            </AdminLayout>
          </AuthGuard>
        }
      />
      <Route
        path='/files/add'
        element={
          <AuthGuard>
            <AdminLayout>
              <PermissionGuard
                permissions={[permissions.InboundCreateInboundFile]}
              >
                <AddFile />
              </PermissionGuard>
            </AdminLayout>
          </AuthGuard>
        }
      />
      <Route
        path='/partners'
        element={
          <AuthGuard>
            <AdminLayout>
              <PermissionGuard permissions={[permissions.PartnerView]}>
                <Partners />
              </PermissionGuard>
            </AdminLayout>
          </AuthGuard>
        }
      />
      <Route
        path='/partners/add'
        element={
          <AuthGuard>
            <AdminLayout>
              <PermissionGuard permissions={[permissions.PartnerCreate]}>
                <AddPartner />
              </PermissionGuard>
            </AdminLayout>
          </AuthGuard>
        }
      />
      <Route
        path='/partners/:id/edit'
        element={
          <AuthGuard>
            <AdminLayout>
              <PermissionGuard permissions={[permissions.PartnerCreate]}>
                <EditPartner />
              </PermissionGuard>
            </AdminLayout>
          </AuthGuard>
        }
      />
      <Route
        path='/products'
        element={
          <AuthGuard>
            <AdminLayout>
              <PermissionGuard permissions={[permissions.ProductView]}>
                <Products />
              </PermissionGuard>
            </AdminLayout>
          </AuthGuard>
        }
      />
      <Route
        path='/products/add'
        element={
          <AuthGuard>
            <AdminLayout>
              <PermissionGuard permissions={[permissions.ProductCreate]}>
                <AddProduct />
              </PermissionGuard>
            </AdminLayout>
          </AuthGuard>
        }
      />
      <Route
        path='/products/:id/edit'
        element={
          <AuthGuard>
            <AdminLayout>
              <PermissionGuard permissions={[permissions.ProductCreate]}>
                <EditProduct />
              </PermissionGuard>
            </AdminLayout>
          </AuthGuard>
        }
      />
      <Route
        path='/skus'
        element={
          <AuthGuard>
            <AdminLayout>
              <SKUs />
            </AdminLayout>
          </AuthGuard>
        }
      />
      <Route
        path='/skus/add'
        element={
          <AuthGuard>
            <AdminLayout>
              <AddSKU />
            </AdminLayout>
          </AuthGuard>
        }
      />
      <Route
        path='/mapping'
        element={
          <AuthGuard>
            <AdminLayout>
              <Mapping />
            </AdminLayout>
          </AuthGuard>
        }
      />
      <Route
        path='/mapping/add'
        element={
          <AuthGuard>
            <AdminLayout>
              <AddMapping />
            </AdminLayout>
          </AuthGuard>
        }
      />
      <Route
        path='/availability-report'
        element={
          <AuthGuard>
            <AdminLayout>
              <PermissionGuard
                permissions={[permissions.ViewProductAvailabilityReport]}
              >
                <AvailabilityReport />
              </PermissionGuard>
            </AdminLayout>
          </AuthGuard>
        }
      />
      <Route
        path='/serial-generator'
        element={
          <AuthGuard>
            <AdminLayout>
              <PermissionGuard permissions={[permissions.UserManagement]}>
                <SerialGenerator />
              </PermissionGuard>
            </AdminLayout>
          </AuthGuard>
        }
      />
      <Route
        path='/expiredItems-report'
        element={
          <AuthGuard>
            <AdminLayout>
              <PermissionGuard
                permissions={[permissions.ExpiredNonSerializedItems]}
              >
                <ExpiredItems />
              </PermissionGuard>
            </AdminLayout>
          </AuthGuard>
        }
      />
      <Route
        path='/activeItems-report'
        element={
          <AuthGuard>
            <AdminLayout>
              <PermissionGuard
                permissions={[permissions.ActiveNonSerializedItems]}
              >
                <ActiveItems />
              </PermissionGuard>
            </AdminLayout>
          </AuthGuard>
        }
      />
      <Route
        path='/shippingReport'
        element={
          <AuthGuard>
            <AdminLayout>
              <PermissionGuard
              //TODO add permission
              // permissions={[permissions.ActiveNonSerializedItems]}
              >
                <ShippingReport />
              </PermissionGuard>
            </AdminLayout>
          </AuthGuard>
        }
      />
      <Route
        path='/auditTrail-report'
        element={
          <AuthGuard>
            <AdminLayout>
              <PermissionGuard permissions={[permissions.AuditTrail]}>
                <AuditTrailReport />
              </PermissionGuard>
            </AdminLayout>
          </AuthGuard>
        }
      />
      <Route
        path='/supplier-report'
        element={
          <AuthGuard>
            <AdminLayout>
              <PermissionGuard
                permissions={[permissions.ViewProductBySupplierReport]}
              >
                <SupplierReport />
              </PermissionGuard>
            </AdminLayout>
          </AuthGuard>
        }
      />
      <Route
        path='/decommission'
        element={
          <AuthGuard>
            <AdminLayout>
              <PermissionGuard permissions={[permissions.ProductCreate]}>
                <Decommission />
              </PermissionGuard>
            </AdminLayout>
          </AuthGuard>
        }
      />{" "}
      //TODO: change permission later
      <Route
        path='/move'
        element={
          <AuthGuard>
            <AdminLayout>
              <PermissionGuard permissions={[permissions.ProductCreate]}>
                <Move />
              </PermissionGuard>
            </AdminLayout>
          </AuthGuard>
        }
      />{" "}
      //TODO: change permission later
      <Route
        path='/quantity-report'
        element={
          <AuthGuard>
            <AdminLayout>
              <PermissionGuard
                permissions={[permissions.ViewProductQuantityReport]}
              >
                <QuantityReport />
              </PermissionGuard>
            </AdminLayout>
          </AuthGuard>
        }
      />
      <Route
        path='/decanting-report'
        element={
          <AuthGuard>
            <AdminLayout>
              <PermissionGuard
                permissions={[permissions.ViewProductQuantityReport]}
              >
                <DecantingReports />
              </PermissionGuard>
            </AdminLayout>
          </AuthGuard>
        }
      />
      <Route
        path='/batch-availability'
        element={
          <AuthGuard>
            <AdminLayout>
              <PermissionGuard
                permissions={[permissions.ViewProductQuantityReport]}
              >
                <BatchReport />
              </PermissionGuard>
            </AdminLayout>
          </AuthGuard>
        }
      />
      <Route
        path='/batch-status'
        element={
          <AuthGuard>
            <AdminLayout>
              <PermissionGuard
                permissions={[permissions.ViewProductQuantityReport]}
              >
                <BatchStatus />
              </PermissionGuard>
            </AdminLayout>
          </AuthGuard>
        }
      />
      <Route
        path='/non-serialized'
        element={
          <AuthGuard>
            <AdminLayout>
              <PermissionGuard
                permissions={[permissions.ViewProductQuantityReport]}
              >
                <NonSerializedStatus />
              </PermissionGuard>
            </AdminLayout>
          </AuthGuard>
        }
      />
      <Route
        path='/roles'
        element={
          <AuthGuard>
            <AdminLayout>
              <PermissionGuard permissions={[permissions.UserManagement]}>
                <Roles />
              </PermissionGuard>
            </AdminLayout>
          </AuthGuard>
        }
      />
      <Route
        path='/roles/add'
        element={
          <AuthGuard>
            <AdminLayout>
              <PermissionGuard permissions={[permissions.UserManagement]}>
                <AddRole />
              </PermissionGuard>
            </AdminLayout>
          </AuthGuard>
        }
      />
      <Route
        path='/roles/:id/Edit'
        element={
          <AuthGuard>
            <AdminLayout>
              <PermissionGuard permissions={[permissions.UserManagement]}>
                <EditRole />
              </PermissionGuard>
            </AdminLayout>
          </AuthGuard>
        }
      />
      <Route
        path='/roles/:id/duplicate'
        element={
          <AuthGuard>
            <AdminLayout>
              <PermissionGuard permissions={[permissions.UserManagement]}>
                <DuplicateRole />
              </PermissionGuard>
            </AdminLayout>
          </AuthGuard>
        }
      />
      <Route
        path='/users'
        element={
          <AuthGuard>
            <AdminLayout>
              <PermissionGuard permissions={[permissions.UserManagement]}>
                <Users />
              </PermissionGuard>
            </AdminLayout>
          </AuthGuard>
        }
      />
      <Route
        path='/users/add'
        element={
          <AuthGuard>
            <AdminLayout>
              <PermissionGuard permissions={[permissions.UserManagement]}>
                <AddUser />
              </PermissionGuard>
            </AdminLayout>
          </AuthGuard>
        }
      />
      <Route
        path='/users/:id/Edit'
        element={
          <AuthGuard>
            <AdminLayout>
              <PermissionGuard permissions={[permissions.UserManagement]}>
                <EditUser />
              </PermissionGuard>
            </AdminLayout>
          </AuthGuard>
        }
      />
      <Route
        path='/verify-status'
        element={
          <AuthGuard>
            <AdminLayout>
              <PermissionGuard permissions={[permissions.Verify_Status]}>
                <VerifyStatus />
              </PermissionGuard>
            </AdminLayout>
          </AuthGuard>
        }
      />
      <Route
        path='/admin-panel'
        element={
          <AuthGuard>
            <AdminLayout>
              <PermissionGuard permission={permissions.Disaggregation}>
                <AdminPanel />
              </PermissionGuard>
            </AdminLayout>
          </AuthGuard>
        }
      />
      <Route
        path='/admin-panel/:id/tenant-settings'
        element={
          <AuthGuard>
            <AdminLayout>
              <PermissionGuard permission={permissions.Disaggregation}>
                <TenantSettings />
              </PermissionGuard>
            </AdminLayout>
          </AuthGuard>
        }
      />
      <Route
        path='/reset-password'
        element={
          <AuthGuard>
            <AdminLayout>
              <ResetPassword />
            </AdminLayout>
          </AuthGuard>
        }
      />
      <Route path='*' element={<Error />} />
    </>
  );
};

export default routes;
